import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ImageIcon, Link2, Loader2, Sparkles, Trash2, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { API_BASE } from '../../config/api';

export const thumbSrc = (url) => (url && url.startsWith('/api/') ? `${API_BASE}${url}` : url);

export const thumbnailErrorMessage = (e) =>
  e.response?.status === 402
    ? 'Not enough Universal Key balance to generate images. Top up in Profile → Universal Key → Add Balance.'
    : e.response?.data?.detail || 'Thumbnail update failed';

// Thumbnail preview + Upload / Generate with AI / Remove for one external course
export default function ExternalCourseThumbnail({ course, token, onChange }) {
  const [busy, setBusy] = useState('');
  const [url, setUrl] = useState('');
  const fileRef = useRef(null);
  const base = `${API_BASE}/api/admin/external-courses/${course.course_id}`;
  const headers = { Authorization: `Bearer ${token}` };

  const run = async (kind, fn) => {
    setBusy(kind);
    try {
      const res = await fn();
      onChange(res.data?.thumbnail_url || null);
      toast.success(kind === 'remove' ? 'Thumbnail removed' : 'Thumbnail updated');
      return true;
    } catch (e) {
      toast.error(thumbnailErrorMessage(e));
      return false;
    } finally {
      setBusy('');
    }
  };

  const upload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5 MB)'); return; }
    const fd = new FormData();
    fd.append('file', file);
    run('upload', () => axios.post(`${base}/thumbnail`, fd, { headers }));
  };

  return (
    <div className="sm:col-span-2" data-testid="ext-thumb-section">
      <p className="text-sm text-zinc-400 mb-2">Thumbnail</p>
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        <div className="w-full sm:w-56 aspect-video rounded-lg overflow-hidden bg-zinc-900 border border-zinc-700 flex items-center justify-center">
          {course.thumbnail_url
            ? <img src={thumbSrc(course.thumbnail_url)} alt="" className="w-full h-full object-cover" data-testid="ext-thumb-preview" />
            : <ImageIcon className="h-8 w-8 text-zinc-600" />}
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="ext-thumb-file" />
          <Button type="button" variant="outline" size="sm" disabled={!!busy} onClick={() => fileRef.current?.click()} className="border-zinc-700" data-testid="ext-thumb-upload">
            {busy === 'upload' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} Upload image
          </Button>
          <Button type="button" size="sm" disabled={!!busy} onClick={() => run('generate', () => axios.post(`${base}/generate-thumbnail`, {}, { headers }))} className="btn-gold" data-testid="ext-thumb-generate">
            {busy === 'generate' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} {busy === 'generate' ? 'Generating…' : 'Generate with AI'}
          </Button>
          {course.thumbnail_url && (
            <Button type="button" variant="ghost" size="sm" disabled={!!busy} onClick={() => run('remove', () => axios.delete(`${base}/thumbnail`, { headers }))} className="text-red-400" data-testid="ext-thumb-remove">
              <Trash2 className="h-4 w-4 mr-1" /> Remove
            </Button>
          )}
          <div className="flex w-full gap-2 mt-1">
            <Input placeholder="…or paste an image link (https://...)" value={url} onChange={(e) => setUrl(e.target.value)}
              className="h-9 bg-zinc-900 border-zinc-700 text-white" data-testid="ext-thumb-url" />
            <Button type="button" variant="outline" size="sm" disabled={!!busy || !url.trim()} className="border-zinc-700 shrink-0" data-testid="ext-thumb-url-apply"
              onClick={() => run('url', () => axios.post(`${base}/thumbnail-from-url`, { url: url.trim() }, { headers })).then((ok) => ok && setUrl(''))}>
              {busy === 'url' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Link2 className="h-4 w-4 mr-1" />} Use link
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
